import { buildGuidedModeObservations } from '../utils/mrx-observations'
import type {
  GuidedAnswer,
  GuidedEscalationResult,
  GuidedModeClassification,
  GuidedModeDefinition,
} from '../types/guided-mode.types'

interface EvaluateGuidedEscalationArgs {
  definition: GuidedModeDefinition
  answers: Record<string, GuidedAnswer>
  classifications: GuidedModeClassification[]
  explicitFocusedOptions: string[]
  originFindingsByOption?: Record<string, string[]>
  allowEmptyOptions?: boolean
}

interface StreamlinedOptionResult {
  name: string
  preselect: boolean
}

interface StreamlinedEvaluationResult {
  comprehensive: boolean
  panels?: unknown
  conditions?: unknown
  thresholdSet?: unknown
}

const getMrxEngine = () => require('@/utils/mrxEngine') as typeof import('@/utils/mrxEngine')

function stringArray(value: unknown, field: string): string[] {
  if (value === undefined) return []
  if (!Array.isArray(value) || value.some(item => typeof item !== 'string')) {
    throw new Error(`Invalid guided escalation ${field}`)
  }
  return value
}

function validateOptions(value: unknown): StreamlinedOptionResult[] {
  if (!Array.isArray(value)) throw new Error('Invalid guided escalation options')
  if (value.some(option => (
    !option
    || typeof option !== 'object'
    || typeof option.name !== 'string'
    || typeof option.preselect !== 'boolean'
  ))) {
    throw new Error('Invalid guided escalation options')
  }
  return value as StreamlinedOptionResult[]
}

function optionalString(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null
  if (typeof value !== 'string') throw new Error(`Invalid guided escalation ${field}`)
  return value
}

function flatClassifications(classifications: GuidedModeClassification[]): Record<string, string> {
  return classifications.reduce<Record<string, string>>((result, classification) => {
    result[classification.name] = classification.value
    return result
  }, {})
}

export async function selectGuidedFocusedOptions({
  definition,
  answers,
  classifications,
  explicitFocusedOptions,
  originFindingsByOption = {},
  allowEmptyOptions = false,
}: EvaluateGuidedEscalationArgs) {
  const { convertFlexi, streamlinedOptions } = getMrxEngine()
  const observations = buildGuidedModeObservations(definition, answers)
  const responses = observations.map(observation => ({
    type: 'response',
    name: observation.name,
    value: String(observation.value),
  }))
  const convertedResponses = await convertFlexi(responses)
  let options = validateOptions(await streamlinedOptions([...classifications, ...convertedResponses]))
  if (!options.length && allowEmptyOptions) {
    options = explicitFocusedOptions.map(name => ({ name, preselect: false }))
  }
  const explicit = new Set(explicitFocusedOptions)
  const available = new Set(options.map(option => option.name))
  const missingExplicit = explicitFocusedOptions.find(option => !available.has(option))
  if (missingExplicit && !allowEmptyOptions) throw new Error(`Unknown focused option ${missingExplicit}`)
  const filtered = new Map<string, Promise<Set<string>>>()
  const guidedOptions = []
  for (const option of options) {
    let selected = option.preselect || explicit.has(option.name)
    const excluded = option.name === 'danger_signs' || explicit.has(option.name)
      ? [] : [...new Set(originFindingsByOption[option.name] || [])].sort()
    if (selected && excluded.length) {
      const key = JSON.stringify(excluded)
      if (!filtered.has(key)) {
        const names = new Set(excluded)
        filtered.set(key, (async () => {
          const converted = await convertFlexi(responses.filter(response => !names.has(response.name)))
          return new Set(validateOptions(await streamlinedOptions([...classifications, ...converted]))
            .filter(value => value.preselect).map(value => value.name))
        })())
      }
      selected = (await filtered.get(key)!).has(option.name)
    }
    guidedOptions.push({ ...option, selected })
  }
  const focusedSelections = guidedOptions
    .filter(option => option.selected)
    .map(option => option.name)
  return { focusedSelections, streamlinedOptions: guidedOptions }
}

export async function evaluateGuidedEscalation(args: EvaluateGuidedEscalationArgs): Promise<GuidedEscalationResult> {
  const { streamlinedEvaluation } = getMrxEngine()
  const { focusedSelections, streamlinedOptions } = await selectGuidedFocusedOptions(args)
  const evaluation = await streamlinedEvaluation({
    classifications: flatClassifications(args.classifications),
    streamlinedSelections: focusedSelections,
  }) as StreamlinedEvaluationResult

  if (!evaluation || typeof evaluation.comprehensive !== 'boolean') {
    throw new Error('Invalid guided escalation evaluation')
  }

  return {
    comprehensive: evaluation.comprehensive,
    panels: stringArray(evaluation.panels, 'panels'),
    conditions: stringArray(evaluation.conditions, 'conditions'),
    focusedSelections,
    streamlinedOptions,
    thresholdSet: optionalString(evaluation.thresholdSet, 'thresholdSet'),
  }
}
